/**
 * Seed content templates for new youth program unit sites
 * Friendly, welcoming placeholder content
 *
 * Notes:
 * - This file assumes UnitType is a string union that you control in @/types/database.
 * - Add the new UnitType values there to match the cases below.
 */

import { BlockType, UnitType } from '@/types/database';

export interface SeedBlock {
  block_type: BlockType;
  content: Record<string, unknown>;
  block_order: number;
}

export interface SeedPage {
  title: string;
  slug: string;
  page_order: number;
  blocks: SeedBlock[];
}

type UnitMeta = {
  orgLabel: string; // e.g., "Scouting America", "Girl Scouts"
  unitLabel: string; // e.g., "Pack", "Troop", "Crew"
  unitFullLabel: string; // e.g., "Cub Scout Pack"
  joinCta: string; // e.g., "Join Our Pack"
  mottoLine?: string; // short tagline fragment
  meetingNoun?: string; // e.g., "pack meeting"
  homeOfferBullets: string[];
  aboutParagraph2?: string;
  joinParagraph: string;
  joinSteps?: string[];
  calendarEvents: Array<{
    id: string;
    title: string;
    date: string;
    time?: string;
    location: string;
  }>;
};

const byUnitType: Record<string, UnitMeta> = {
  // -----------------------
  // Scouting America (BSA)
  // -----------------------
  cub_scout_pack: {
    orgLabel: 'Scouting America',
    unitLabel: 'Pack',
    unitFullLabel: 'Cub Scout Pack',
    joinCta: 'Join Our Pack',
    mottoLine: 'Where Adventure Begins',
    meetingNoun: 'pack meeting',
    homeOfferBullets: [
      '🏕️ Outdoor adventures and family campouts',
      '🤝 Friendships and belonging',
      '🎯 Age-appropriate skill building',
      '🌟 Character, confidence, and teamwork',
      '👨‍👩‍👧‍👦 Family-friendly activities',
    ],
    aboutParagraph2:
      `Our program is organized by dens, usually by grade. Cub Scouts follow the advancement trail through Lion, Tiger, Wolf, Bear, Webelos, and Arrow of Light, with activities designed for each age.`,
    joinParagraph:
      `Cub Scouting is for families with kids in kindergarten through 5th grade. The best way to get started is to visit a pack activity and meet our leaders and families.`,
    joinSteps: [
      '1. Contact us using the form below',
      `2. Visit a pack meeting to see what we're all about`,
      '3. Complete registration',
      '4. Start having fun with your new Scout friends',
    ],
    calendarEvents: [
      { id: '1', title: 'Pack Meeting', date: 'First Thursday of each month', time: '6:30 PM', location: 'Community Center' },
      { id: '2', title: 'Family Campout', date: 'Coming Soon!', location: 'Check back for details' },
    ],
  },

  scouts_bsa_troop: {
    orgLabel: 'Scouting America',
    unitLabel: 'Troop',
    unitFullLabel: 'Scouts BSA Troop',
    joinCta: 'Join Our Troop',
    mottoLine: 'Prepared. For Life.',
    meetingNoun: 'troop meeting',
    homeOfferBullets: [
      '🏔️ Campouts and high-adventure outings',
      '🦅 Path to Eagle Scout',
      '🎖️ Merit badges and skill mastery',
      '👥 Youth-led leadership experience',
      '🌍 Service and community impact',
    ],
    aboutParagraph2:
      `Scouts BSA is built around the patrol method. Scouts plan activities, lead meetings, and grow through hands-on experiences in the outdoors and in service.`,
    joinParagraph:
      `Scouts BSA is for youth ages 11–17 (or age 10 and completed 5th grade). Whether you're crossing over or joining for the first time, we'd like to meet you.`,
    joinSteps: [
      '1. Reach out using the contact form',
      '2. Visit a troop meeting and meet our Scouts',
      '3. Learn about our calendar and expectations',
      '4. Complete registration and join the adventure',
    ],
    calendarEvents: [
      { id: '1', title: 'Troop Meeting', date: 'Every Tuesday', time: '7:00 PM', location: 'Scout Hut' },
      { id: '2', title: 'Monthly Campout', date: 'Third weekend of each month', location: 'Various locations' },
    ],
  },

  venturing_crew: {
    orgLabel: 'Scouting America',
    unitLabel: 'Crew',
    unitFullLabel: 'Venturing Crew',
    joinCta: 'Join Our Crew',
    mottoLine: 'Adventure. Leadership. Service.',
    meetingNoun: 'crew meeting',
    homeOfferBullets: [
      '🧭 High-adventure and specialty activities',
      '🧑‍🤝‍🧑 Co-ed leadership and teamwork',
      '🏕️ Trip planning led by youth officers',
      '🤲 Service projects with real impact',
      '🎯 Optional awards and skill goals',
    ],
    aboutParagraph2:
      `Venturing is designed for older youth and is typically organized around officers and activity teams rather than patrols. Crews choose a focus and build their calendar around it.`,
    joinParagraph:
      `Venturing is for youth ages 14–20 (and can include 13-year-olds who have completed 8th grade, depending on local policy). Come visit a crew meeting to see what we do.`,
    joinSteps: [
      '1. Contact us using the form below',
      '2. Visit a crew meeting or activity',
      '3. Meet the youth officers and advisors',
      '4. Complete registration and get plugged in',
    ],
    calendarEvents: [
      { id: '1', title: 'Crew Meeting', date: 'Every other week', time: '7:00 PM', location: 'Community Center' },
      { id: '2', title: 'Adventure/Service Outing', date: 'Monthly', location: 'Varies by activity' },
    ],
  },

  sea_scout_ship: {
    orgLabel: 'Scouting America',
    unitLabel: 'Ship',
    unitFullLabel: 'Sea Scout Ship',
    joinCta: 'Join Our Ship',
    mottoLine: 'Seamanship. Leadership. Adventure.',
    meetingNoun: 'ship meeting',
    homeOfferBullets: [
      '⚓ Seamanship skills and on-the-water time',
      '⛵ Sailing, boating, and maritime safety',
      '🧑‍✈️ Youth-led leadership and training',
      '🌊 Service and stewardship on waterways',
      '🎖️ Advancement opportunities up to Quartermaster',
    ],
    aboutParagraph2:
      `Sea Scouts are typically organized with youth officers and ship-specific divisions or activity teams. The program is maritime-focused, with a strong emphasis on safety and skills.`,
    joinParagraph:
      `Sea Scouts is for youth ages 14–20. If you’re interested in sailing, boating, navigation, or maritime service, come meet us.`,
    joinSteps: [
      '1. Send us a message',
      '2. Visit a ship meeting and meet the youth leaders',
      '3. Review safety expectations and training',
      '4. Complete registration and get on the water',
    ],
    calendarEvents: [
      { id: '1', title: 'Ship Meeting', date: 'Weekly', time: '7:00 PM', location: 'Marina classroom / meeting room' },
      { id: '2', title: 'On-the-Water Day', date: 'Twice monthly', location: 'Local marina / waterfront' },
    ],
  },

  exploring_post: {
    orgLabel: 'Scouting America',
    unitLabel: 'Post',
    unitFullLabel: 'Exploring Post',
    joinCta: 'Join Our Post',
    mottoLine: 'Career Exploration, Hands-On Experience',
    meetingNoun: 'post meeting',
    homeOfferBullets: [
      '🧪 Hands-on career exploration experiences',
      '👥 Mentorship from professionals',
      '🏢 Site visits, ride-alongs, and workshops',
      '📈 Employability and leadership skills',
      '🤝 Community connection and networking',
    ],
    aboutParagraph2:
      `Exploring is career-focused and is often organized around teams or committees depending on the field. Posts are commonly sponsored by employers, agencies, and community organizations.`,
    joinParagraph:
      `Exploring is for youth ages 10–20. If you’re curious about a career path and want real-world exposure, come check it out.`,
    joinSteps: [
      '1. Contact us using the form below',
      '2. Attend an orientation meeting',
      '3. Review participation and safety expectations',
      '4. Complete registration and start participating',
    ],
    calendarEvents: [
      { id: '1', title: 'Post Meeting / Workshop', date: 'Monthly', time: '6:30 PM', location: 'Host organization site' },
      { id: '2', title: 'Field Experience', date: 'Quarterly', location: 'Varies by program' },
    ],
  },

  exploring_club: {
    orgLabel: 'Scouting America',
    unitLabel: 'Club',
    unitFullLabel: 'Exploring Club',
    joinCta: 'Join Our Club',
    mottoLine: 'Explore Careers, Build Skills',
    meetingNoun: 'club meeting',
    homeOfferBullets: [
      '🔍 Career exploration designed for younger youth',
      '🧠 Skills workshops and guest speakers',
      '👥 Team activities and leadership practice',
      '🏢 Site visits and hands-on demos',
      '🤝 A supportive peer community',
    ],
    aboutParagraph2:
      `Exploring Clubs are similar to Posts but are designed for middle-school ages. Structure is flexible and depends on the host organization and program theme.`,
    joinParagraph:
      `Exploring Clubs are commonly used for middle school youth (often ages ~10–14). If your child wants to explore careers and skills, come visit a club meeting.`,
    joinSteps: [
      '1. Contact us',
      '2. Attend a club meeting',
      '3. Meet the adult mentors and youth leaders',
      '4. Complete registration and participate',
    ],
    calendarEvents: [
      { id: '1', title: 'Club Meeting', date: 'Biweekly', time: '6:00 PM', location: 'School / community site' },
      { id: '2', title: 'Career Day / Visit', date: 'Seasonal', location: 'Varies by program' },
    ],
  },

  // -----------------------
  // Girl Scouts of the USA
  // -----------------------
  girl_scout_troop: {
    orgLabel: 'Girl Scouts (GSUSA)',
    unitLabel: 'Troop',
    unitFullLabel: 'Girl Scout Troop',
    joinCta: 'Join Our Troop',
    mottoLine: 'Make New Friends, Build Confidence',
    meetingNoun: 'troop meeting',
    homeOfferBullets: [
      '🧭 Adventures that build courage and confidence',
      '🤝 Friendships and belonging',
      '🧠 Skills, badges, and leadership opportunities',
      '🌍 Community service and action projects',
      '🎨 A mix of outdoors, STEM, creativity, and more',
    ],
    aboutParagraph2:
      `Girl Scout troops can be single-grade or multi-level, spanning Daisy, Brownie, Junior, Cadette, Senior, and Ambassador. Troop structure is flexible and adapts to the girls’ interests and goals.`,
    joinParagraph:
      `Girl Scouts is for youth in grades K–12. If you’re looking for a troop community and experiences that grow with your child, visit a meeting and see if it’s a fit.`,
    joinSteps: [
      '1. Contact us using the form below',
      '2. Visit a troop meeting',
      '3. Learn about troop activities and expectations',
      '4. Complete registration and get started',
    ],
    calendarEvents: [
      { id: '1', title: 'Troop Meeting', date: 'Weekly', time: '6:00 PM', location: 'School / community room' },
      { id: '2', title: 'Service or Badge Event', date: 'Monthly', location: 'Varies' },
    ],
  },

  // -----------------------
  // Trail Life USA
  // -----------------------
  trail_life_troop: {
    orgLabel: 'Trail Life USA',
    unitLabel: 'Troop',
    unitFullLabel: 'Trail Life Troop',
    joinCta: 'Join Our Troop',
    mottoLine: 'Outdoor Skills and Character',
    meetingNoun: 'troop meeting',
    homeOfferBullets: [
      '🏕️ Outdoor skills and camping',
      '🧭 Patrol-based teamwork and leadership',
      '🧱 Character development and service',
      '🛠️ Practical skills and challenges',
      '👥 A supportive community for boys and families',
    ],
    aboutParagraph2:
      `Trail Life troops often organize by patrols and may group by program division (Woodlands Trail, Navigators, Adventurers) while still operating as one troop community.`,
    joinParagraph:
      `Trail Life is commonly organized for boys in grades K–12. Visit a troop meeting to learn how we run our program and what to expect.`,
    joinSteps: [
      '1. Send us a message',
      '2. Visit a troop meeting',
      '3. Meet leaders and patrol members',
      '4. Complete registration and participate',
    ],
    calendarEvents: [
      { id: '1', title: 'Troop Meeting', date: 'Weekly', time: '6:30 PM', location: 'Host church / community site' },
      { id: '2', title: 'Outdoor Outing', date: 'Monthly', location: 'Varies' },
    ],
  },

  // -----------------------
  // American Heritage Girls
  // -----------------------
  ahg_troop: {
    orgLabel: 'American Heritage Girls',
    unitLabel: 'Troop',
    unitFullLabel: 'AHG Troop',
    joinCta: 'Join Our Troop',
    mottoLine: 'Faith, Service, and Leadership',
    meetingNoun: 'troop meeting',
    homeOfferBullets: [
      '🤝 Community, friendships, and mentoring',
      '🌟 Leadership and life skills',
      '🎖️ Badges and program experiences',
      '🤲 Service projects and community impact',
      '🏕️ Outdoor and enrichment activities',
    ],
    aboutParagraph2:
      `AHG troops typically group girls by program level (Pathfinder, Tenderheart, Explorer, Pioneer, Patriot) within one troop community. Activities are designed to be age-appropriate and girl-led over time.`,
    joinParagraph:
      `AHG is commonly organized for girls in grades K–12. The best way to explore is to visit a troop meeting and meet our leaders and families.`,
    joinSteps: [
      '1. Contact us using the form below',
      '2. Visit a troop meeting',
      '3. Learn about troop structure and activities',
      '4. Complete registration and get started',
    ],
    calendarEvents: [
      { id: '1', title: 'Troop Meeting', date: 'Weekly', time: '6:00 PM', location: 'Host church / community site' },
      { id: '2', title: 'Service / Badge Event', date: 'Monthly', location: 'Varies' },
    ],
  },
};

function buildGenericSeedPages(meta: UnitMeta, unitName: string, unitNumber: string): SeedPage[] {
  const unitLine = `${meta.unitLabel} ${unitNumber}`;
  const orgLine = meta.orgLabel ? `${meta.orgLabel} • ${unitLine}` : unitLine;

  return [
    {
      title: 'Home',
      slug: 'home',
      page_order: 0,
      blocks: [
        {
          block_type: 'hero',
          content: {
            headline: `Welcome to ${unitName}!`,
            subheadline: meta.mottoLine ? `${unitLine} — ${meta.mottoLine}` : orgLine,
            buttonText: meta.joinCta,
            buttonLink: '/join',
          },
          block_order: 0,
        },
        {
          block_type: 'text',
          content: {
            content:
              `We're glad you found us. ${unitName} is a welcoming community for youth and families. ` +
              `We focus on growth through activities, service, and shared experiences.`,
          },
          block_order: 1,
        },
        {
          block_type: 'list',
          content: {
            heading: 'What You Can Expect',
            items: meta.homeOfferBullets,
          },
          block_order: 2,
        },
      ],
    },
    {
      title: `About Our ${meta.unitLabel}`,
      slug: 'about',
      page_order: 1,
      blocks: [
        {
          block_type: 'hero',
          content: {
            headline: `About Our ${meta.unitLabel}`,
            subheadline: 'Who we are and how we operate',
          },
          block_order: 0,
        },
        {
          block_type: 'text',
          content: {
            content:
              `${unitName} serves local youth and families with consistent programming and caring adult volunteers. ` +
              `We aim to create a safe, inclusive environment where young people can try new things and build skills.`,
          },
          block_order: 1,
        },
        ...(meta.aboutParagraph2
          ? [
              {
                block_type: 'text' as const,
                content: { content: meta.aboutParagraph2 },
                block_order: 2,
              },
            ]
          : []),
      ],
    },
    {
      title: 'Calendar & Events',
      slug: 'calendar',
      page_order: 2,
      blocks: [
        {
          block_type: 'hero',
          content: {
            headline: 'Upcoming Events',
            subheadline: "Here’s what we have planned.",
          },
          block_order: 0,
        },
        {
          block_type: 'calendar',
          content: {
            events: meta.calendarEvents,
          },
          block_order: 1,
        },
        {
          block_type: 'text',
          content: {
            content:
              `We update this calendar as plans are finalized. If you're visiting for the first time, ` +
              `contact us so we can share the best event to attend.`,
          },
          block_order: 2,
        },
      ],
    },
    {
      title: 'Join',
      slug: 'join',
      page_order: 3,
      blocks: [
        {
          block_type: 'hero',
          content: {
            headline: meta.joinCta,
            subheadline: 'Start with a visit and meet the community',
          },
          block_order: 0,
        },
        {
          block_type: 'text',
          content: {
            content: meta.joinParagraph,
          },
          block_order: 1,
        },
        ...(meta.joinSteps
          ? [
              {
                block_type: 'list' as const,
                content: {
                  heading: 'How to Get Started',
                  items: meta.joinSteps,
                },
                block_order: 2,
              },
            ]
          : []),
        {
          block_type: 'button',
          content: {
            text: 'Contact Us to Get Started',
            link: '/contact',
            variant: 'primary',
          },
          block_order: meta.joinSteps ? 3 : 2,
        },
      ],
    },
    {
      title: 'Contact',
      slug: 'contact',
      page_order: 4,
      blocks: [
        {
          block_type: 'hero',
          content: {
            headline: 'Contact Us',
            subheadline: `Questions about ${unitLine}? Send a message.`,
          },
          block_order: 0,
        },
        {
          block_type: 'text',
          content: {
            content:
              `If you'd like to visit a ${meta.meetingNoun ?? 'meeting'} or have questions about membership, ` +
              `fill out the form below and a leader will respond.`,
          },
          block_order: 1,
        },
        {
          block_type: 'contact',
          content: {
            heading: 'Send Us a Message',
            description: "We'll respond soon.",
            showName: true,
            showEmail: true,
            showMessage: true,
          },
          block_order: 2,
        },
      ],
    },
  ];
}

export function getSeedContent(unitType: UnitType, unitName: string, unitNumber: string): SeedPage[] {
  const meta = byUnitType[unitType as string];

  // Backward-compatible fallback behavior:
  const fallbackMeta = byUnitType['scouts_bsa_troop'];

  return buildGenericSeedPages(meta ?? fallbackMeta, unitName, unitNumber);
}

export function getUnitTypeLabel(unitType: UnitType): string {
  const meta = byUnitType[unitType as string];
  return meta?.unitLabel ?? 'Troop';
}

export function getUnitTypeFullLabel(unitType: UnitType): string {
  const meta = byUnitType[unitType as string];
  return meta?.unitFullLabel ?? 'Scouts BSA Troop';
}

export function getOrganizationLabel(unitType: UnitType): string {
  const meta = byUnitType[unitType as string];
  return meta?.orgLabel ?? 'Scouting America';
}
